/* eslint-disable */
import Long from "long";
import _m0 from "protobufjs/minimal";

export const protobufPackage = "dymensionxyz.dymension.sponsorship";

/** Params is a module parameters. */
export interface Params {
  /**
   * MinAllocationWeight is a minimum portion of the user's voting power that
   * one can allocate to a single gauge. The value must fall between 0 and 100,
   * inclusively. For example, if this parameter is 20%, then the min allocation
   * is 20%, and consequently, the user can vote on a max of 5 gauges (100 / 20
   * = 5).
   */
  minAllocationWeight: string;
  /**
   * MinVotingPower is a minimum voting power a user must have in order to be
   * able to vote.
   */
  minVotingPower: string;
}

/**
 * Distribution holds the distribution plan among gauges. Distribution with the
 * Merge operation forms an Abelian group:
 * https://en.wikipedia.org/wiki/Abelian_group. Which helps to safely operate
 * with it. That is, Distribution:
 *   1. Is commutative:           a + b = b + a
 *   2. Is associative:           a + (b + c) = (a + b) + c
 *   3. Has the identity element: e + a = a + e = a
 *   4. Has inverse elements:     i + a = a + i = e
 * where
 * a, b, c, i, e : Distribution type,
 * + : Merge operation (Merge method)
 * i : inverse of a (Negate method),
 * e : identity element (zero, NewDistribution method).
 *
 * Example 1:
 *   a     : [100, 1] [50, 2] [0, 3] power 100
 *   b     : [160, 1] [40, 2] [5, 3] power 200
 *   a + b : [260, 1] [90, 2] [5, 3] power 300
 *
 * Example 2:
 *   a     : [100, 1] [50, 2] [0, 3] power 100
 *   b     : [160, 1]                power 200
 *   a + b : [260, 1] [50, 2] [0, 3] power 300
 *
 * Example 3:
 *   a     : [100, 1] [50, 2] [0, 3]          power 100
 *   b     :                         [160, 4] power 200
 *   a + b : [100, 1] [50, 2] [0, 3] [160, 4] power 300
 *
 * Example 4:
 *   a     : [210, 1] [180, 2] [210, 3] power 600
 *   -b    : [-40, 1] [-10, 2]          power -50
 *   a - b : [170, 1] [180, 2] [210, 3] power 550
 *
 * Example 5:
 *   a         : [210, 1] [180, 2] [210, 3] power 600
 *   e         :                            power 0
 *   a + e = a : [210, 1] [180, 2] [210, 3] power 600
 *
 * Example 6:
 *   a         : [ 210, 1] [ 180, 2] [ 210, 3] power  600
 *   i = -a    : [-210, 1] [-180, 2] [-210, 3] power -600
 *   a + i = e :                               power 0
 *
 * CONTRACT: Gauges are sorted by the gauge ID.
 * CONTRACT: Gauges hold gauges only with non-zero power.
 */
export interface Distribution {
  /** VotingPower is the total voting power that the distribution holds. */
  votingPower: string;
  /** Gauges is a breakdown of the voting power for different gauges. */
  gauges: Gauge[];
}

/** Gauge represents a single gauge with its absolute power. */
export interface Gauge {
  /** GaugeID is the ID of the gauge. */
  gaugeId: number;
  /** Power is a total voting power distributed to this gauge. */
  power: string;
}

/** Vote represents the user's vote. */
export interface Vote {
  /** Voting power is a total voting power of the vote. */
  votingPower: string;
  /** Weights is a breakdown of the vote for different gauges. */
  weights: GaugeWeight[];
}

/** GaugeWeight is a weight distributed to the specified gauge. */
export interface GaugeWeight {
  /** GaugeID is the ID of the gauge. */
  gaugeId: number;
  /**
   * Weight is a portion of the voting power that is allocated for the given
   * gauge. The value must fall between Params.MinAllocationWeight and 100,
   * inclusive.
   */
  weight: string;
}

function createBaseParams(): Params {
  return { minAllocationWeight: "", minVotingPower: "" };
}

export const Params = {
  encode(message: Params, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.minAllocationWeight !== "") {
      writer.uint32(10).string(message.minAllocationWeight);
    }
    if (message.minVotingPower !== "") {
      writer.uint32(18).string(message.minVotingPower);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Params {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseParams();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.minAllocationWeight = reader.string();
          break;
        case 2:
          message.minVotingPower = reader.string();
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): Params {
    return {
      minAllocationWeight: isSet(object.minAllocationWeight) ? String(object.minAllocationWeight) : "",
      minVotingPower: isSet(object.minVotingPower) ? String(object.minVotingPower) : "",
    };
  },

  toJSON(message: Params): unknown {
    const obj: any = {};
    message.minAllocationWeight !== undefined && (obj.minAllocationWeight = message.minAllocationWeight);
    message.minVotingPower !== undefined && (obj.minVotingPower = message.minVotingPower);
    return obj;
  },

  fromPartial<I extends Exact<DeepPartial<Params>, I>>(object: I): Params {
    const message = createBaseParams();
    message.minAllocationWeight = object.minAllocationWeight ?? "";
    message.minVotingPower = object.minVotingPower ?? "";
    return message;
  },
};

function createBaseDistribution(): Distribution {
  return { votingPower: "", gauges: [] };
}

export const Distribution = {
  encode(message: Distribution, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.votingPower !== "") {
      writer.uint32(10).string(message.votingPower);
    }
    for (const v of message.gauges) {
      Gauge.encode(v!, writer.uint32(18).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Distribution {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseDistribution();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.votingPower = reader.string();
          break;
        case 2:
          message.gauges.push(Gauge.decode(reader, reader.uint32()));
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): Distribution {
    return {
      votingPower: isSet(object.votingPower) ? String(object.votingPower) : "",
      gauges: Array.isArray(object?.gauges) ? object.gauges.map((e: any) => Gauge.fromJSON(e)) : [],
    };
  },

  toJSON(message: Distribution): unknown {
    const obj: any = {};
    message.votingPower !== undefined && (obj.votingPower = message.votingPower);
    if (message.gauges) {
      obj.gauges = message.gauges.map((e) => e ? Gauge.toJSON(e) : undefined);
    } else {
      obj.gauges = [];
    }
    return obj;
  },

  fromPartial<I extends Exact<DeepPartial<Distribution>, I>>(object: I): Distribution {
    const message = createBaseDistribution();
    message.votingPower = object.votingPower ?? "";
    message.gauges = object.gauges?.map((e) => Gauge.fromPartial(e)) || [];
    return message;
  },
};

function createBaseGauge(): Gauge {
  return { gaugeId: 0, power: "" };
}

export const Gauge = {
  encode(message: Gauge, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.gaugeId !== 0) {
      writer.uint32(8).uint64(message.gaugeId);
    }
    if (message.power !== "") {
      writer.uint32(18).string(message.power);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Gauge {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseGauge();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.gaugeId = longToNumber(reader.uint64() as Long);
          break;
        case 2:
          message.power = reader.string();
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): Gauge {
    return {
      gaugeId: isSet(object.gaugeId) ? Number(object.gaugeId) : 0,
      power: isSet(object.power) ? String(object.power) : "",
    };
  },

  toJSON(message: Gauge): unknown {
    const obj: any = {};
    message.gaugeId !== undefined && (obj.gaugeId = Math.round(message.gaugeId));
    message.power !== undefined && (obj.power = message.power);
    return obj;
  },

  fromPartial<I extends Exact<DeepPartial<Gauge>, I>>(object: I): Gauge {
    const message = createBaseGauge();
    message.gaugeId = object.gaugeId ?? 0;
    message.power = object.power ?? "";
    return message;
  },
};

function createBaseVote(): Vote {
  return { votingPower: "", weights: [] };
}

export const Vote = {
  encode(message: Vote, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.votingPower !== "") {
      writer.uint32(10).string(message.votingPower);
    }
    for (const v of message.weights) {
      GaugeWeight.encode(v!, writer.uint32(18).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): Vote {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseVote();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.votingPower = reader.string();
          break;
        case 2:
          message.weights.push(GaugeWeight.decode(reader, reader.uint32()));
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): Vote {
    return {
      votingPower: isSet(object.votingPower) ? String(object.votingPower) : "",
      weights: Array.isArray(object?.weights) ? object.weights.map((e: any) => GaugeWeight.fromJSON(e)) : [],
    };
  },

  toJSON(message: Vote): unknown {
    const obj: any = {};
    message.votingPower !== undefined && (obj.votingPower = message.votingPower);
    if (message.weights) {
      obj.weights = message.weights.map((e) => e ? GaugeWeight.toJSON(e) : undefined);
    } else {
      obj.weights = [];
    }
    return obj;
  },

  fromPartial<I extends Exact<DeepPartial<Vote>, I>>(object: I): Vote {
    const message = createBaseVote();
    message.votingPower = object.votingPower ?? "";
    message.weights = object.weights?.map((e) => GaugeWeight.fromPartial(e)) || [];
    return message;
  },
};

function createBaseGaugeWeight(): GaugeWeight {
  return { gaugeId: 0, weight: "" };
}

export const GaugeWeight = {
  encode(message: GaugeWeight, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.gaugeId !== 0) {
      writer.uint32(8).uint64(message.gaugeId);
    }
    if (message.weight !== "") {
      writer.uint32(18).string(message.weight);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): GaugeWeight {
    const reader = input instanceof _m0.Reader ? input : new _m0.Reader(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseGaugeWeight();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          message.gaugeId = longToNumber(reader.uint64() as Long);
          break;
        case 2:
          message.weight = reader.string();
          break;
        default:
          reader.skipType(tag & 7);
          break;
      }
    }
    return message;
  },

  fromJSON(object: any): GaugeWeight {
    return {
      gaugeId: isSet(object.gaugeId) ? Number(object.gaugeId) : 0,
      weight: isSet(object.weight) ? String(object.weight) : "",
    };
  },

  toJSON(message: GaugeWeight): unknown {
    const obj: any = {};
    message.gaugeId !== undefined && (obj.gaugeId = Math.round(message.gaugeId));
    message.weight !== undefined && (obj.weight = message.weight);
    return obj;
  },

  fromPartial<I extends Exact<DeepPartial<GaugeWeight>, I>>(object: I): GaugeWeight {
    const message = createBaseGaugeWeight();
    message.gaugeId = object.gaugeId ?? 0;
    message.weight = object.weight ?? "";
    return message;
  },
};

declare var self: any | undefined;
declare var window: any | undefined;
declare var global: any | undefined;
var globalThis: any = (() => {
  if (typeof globalThis !== "undefined") {
    return globalThis;
  }
  if (typeof self !== "undefined") {
    return self;
  }
  if (typeof window !== "undefined") {
    return window;
  }
  if (typeof global !== "undefined") {
    return global;
  }
  throw "Unable to locate global object";
})();

type Builtin = Date | Function | Uint8Array | string | number | boolean | undefined;

export type DeepPartial<T> = T extends Builtin ? T
  : T extends Array<infer U> ? Array<DeepPartial<U>> : T extends ReadonlyArray<infer U> ? ReadonlyArray<DeepPartial<U>>
  : T extends {} ? { [K in keyof T]?: DeepPartial<T[K]> }
  : Partial<T>;

type KeysOfUnion<T> = T extends T ? keyof T : never;
export type Exact<P, I extends P> = P extends Builtin ? P
  : P & { [K in keyof P]: Exact<P[K], I[K]> } & { [K in Exclude<keyof I, KeysOfUnion<P>>]: never };

function longToNumber(long: Long): number {
  if (long.gt(Number.MAX_SAFE_INTEGER)) {
    throw new globalThis.Error("Value is larger than Number.MAX_SAFE_INTEGER");
  }
  return long.toNumber();
}

if (_m0.util.Long !== Long) {
  _m0.util.Long = Long as any;
  _m0.configure();
}

function isSet(value: any): boolean {
  return value !== null && value !== undefined;
}
